/**
 * AUTHENTICATION SERVICE
 *
 * Provides functions to get Azure AD access tokens for API calls.
 * Works with MSAL (Microsoft Authentication Library) to silently
 * acquire tokens when needed.
 */

import { loginRequest } from '../authConfig';

// MSAL instance - shared across the app
let msalInstance = null;

/**
 * Initialize the MSAL instance
 * Call this once when the app starts
 *
 * @param {PublicClientApplication} instance - MSAL instance from MsalProvider
 */
export const initializeMsal = (instance) => {
    msalInstance = instance;
};

/**
 * Get the current MSAL instance
 *
 * @returns {PublicClientApplication} MSAL instance
 */
export const getMsalInstance = () => {
    return msalInstance;
};

/**
 * Get access token for API calls
 * Attempts silent token acquisition first, falls back to interactive if needed
 *
 * @returns {Promise<string|null>} Access token or null if not authenticated
 */
export const getAccessToken = async () => {
    if (!msalInstance) {
        console.error('[AUTH SERVICE] MSAL instance not initialized');
        return null;
    }

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
        return null;
    }

    try {
        // Try to get token silently
        const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
        });

        return response.accessToken;
    } catch (error) {
        console.error('[AUTH SERVICE] Silent token acquisition failed:', error);

        // If silent fails, try interactive
        // This might happen if the token is expired and refresh token is also expired
        try {
            const response = await msalInstance.acquireTokenPopup(loginRequest);
            return response.accessToken;
        } catch (interactiveError) {
            console.error('[AUTH SERVICE] Interactive token acquisition failed:', interactiveError);
            return null;
        }
    }
};

/**
 * Get ID token for API calls (alternative to access token)
 * ID tokens contain user identity claims
 *
 * @returns {Promise<string|null>} ID token or null if not authenticated
 */
export const getIdToken = async () => {
    if (!msalInstance) {
        console.error('[AUTH SERVICE] MSAL instance not initialized');
        return null;
    }

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
        return null;
    }

    try {
        const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
        });

        return response.idToken;
    } catch (error) {
        return null;
    }
};

/**
 * Make an authenticated fetch request
 * Automatically includes the Authorization header with the ID token
 * (Using ID token since we're a SPA and don't have a backend API scope)
 *
 * Handles 401 errors by attempting to refresh the token and retry once.
 *
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Response>} Fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
    // Use ID token instead of access token for SPA authentication
    let token = await getIdToken();

    if (!token) {
        throw new Error('Not authenticated');
    }

    const headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
    };

    const response = await fetch(url, {
        ...options,
        headers
    });

    // If we get a 401, try to refresh the token and retry once
    if (response.status === 401) {
        // Force a fresh token by using acquireTokenSilent with forceRefresh
        // or fall back to interactive if needed
        try {
            token = await forceTokenRefresh();

            if (token) {
                const retryHeaders = {
                    ...options.headers,
                    'Authorization': `Bearer ${token}`
                };

                return fetch(url, {
                    ...options,
                    headers: retryHeaders
                });
            }
        } catch (refreshError) {
            // Token refresh failed, return original 401 response
        }
    }

    return response;
};

/**
 * Force a token refresh by clearing cache and re-acquiring
 *
 * @returns {Promise<string|null>} New token or null
 */
const forceTokenRefresh = async () => {
    if (!msalInstance) {
        console.error('[AUTH SERVICE] MSAL instance not initialized');
        return null;
    }

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
        return null;
    }

    try {
        // Try silent acquisition with force refresh
        const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
            forceRefresh: true
        });

        return response.idToken;
    } catch (error) {
        // If silent fails, try interactive popup
        try {
            const response = await msalInstance.acquireTokenPopup(loginRequest);
            return response.idToken;
        } catch (interactiveError) {
            return null;
        }
    }
};

/**
 * Get current user info from MSAL
 *
 * @returns {object|null} User info or null if not authenticated
 */
export const getCurrentUser = () => {
    if (!msalInstance) {
        return null;
    }

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
        return null;
    }

    const account = accounts[0];

    return {
        id: account.localAccountId,
        email: account.username,
        name: account.name,
        tenantId: account.tenantId
    };
};
